import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Settings, Palette, FormInput, LayoutDashboard, NewspaperIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PageSections } from "./PageSections";
import { FormElements } from "./FormElements";
import { ProFeatureWrapper } from "./ProFeatureWrapper";
import { ThemeSelector } from "./ThemeComponents/ThemeSelector";
import { PagesList } from "./PagesList";
import { BlogTab } from "./BlogTab";
import { useToast } from "@/components/ui/use-toast";
import { loadStripe } from '@stripe/stripe-js';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ImageDropzone } from "../../components/ImageDropzone";
import type { FunnelData } from "./types";
import { defaultFunnelData } from "./constants";
import { GeneralTabContent } from "./TabContent/GeneralTabContent";

interface FunnelFormProps {
  onUpdate: (data: FunnelData) => void;
  initialData?: FunnelData;
  onPageSelect?: (pageId: string) => void;
  handleImageUpload?: (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'headerImage') => void;
}

export const FunnelForm = ({
  onUpdate,
  initialData,
  onPageSelect,
  handleImageUpload
}: FunnelFormProps) => {
  const [funnelData, setFunnelData] = useState<FunnelData>(initialData || defaultFunnelData);
  const { toast } = useToast();
  const [isUpgrading, setIsUpgrading] = useState(false);

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const configDoc = await getDoc(doc(db, 'config', 'stripe'));
      if (!configDoc.exists()) {
        throw new Error('Stripe configuration not found');
      }
      const config = configDoc.data();
      if (!config?.publishableKey || !config?.priceId) {
        throw new Error('Invalid Stripe configuration');
      }
      const stripe = await loadStripe(config.publishableKey);
      if (!stripe) throw new Error('Failed to load Stripe');
      const {
        error
      } = await stripe.redirectToCheckout({
        lineItems: [{
          price: config.priceId,
          quantity: 1
        }],
        mode: 'payment',
        successUrl: `${window.location.origin}/payment-success?funnelId=${funnelData.id}`,
        cancelUrl: `${window.location.origin}/pricing`
      });
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error starting checkout:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to start checkout process",
        variant: "destructive"
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  const updateFunnelData = (updates: Partial<FunnelData>) => {
    const newData = {
      ...funnelData,
      ...updates
    };
    setFunnelData(newData);
    onUpdate(newData);
  };

  const handleFinishPageChange = (pageId: string) => {
    updateFunnelData({
      finishPageId: pageId
    });
  };

  const handleBlogPageChange = (pageId: string) => {
    updateFunnelData({
      blogPageId: pageId
    });
  };

  return <div className="w-full p-4 space-y-6 animate-fade-in px-[10px] py-0 my-[10px]">
      <Tabs defaultValue="settings" className="w-full">
        <TabsList className="w-full mb-4 grid grid-cols-5">
          <TabsTrigger value="settings" className="flex flex-col items-center gap-2 flex-1">
            <Settings className="h-5 w-5" />
            <span className="text-xs">Beállítások</span>
          </TabsTrigger>
          <TabsTrigger value="pages" className="flex flex-col items-center gap-2 flex-1">
            <LayoutDashboard className="h-5 w-5" />
            <span className="text-xs">Oldalak</span>
          </TabsTrigger>
          <TabsTrigger value="design" className="flex flex-col items-center gap-2 flex-1">
            <Palette className="h-5 w-5" />
            <span className="text-xs">Témák</span>
          </TabsTrigger>
          <TabsTrigger value="form" className="flex flex-col items-center gap-2 flex-1">
            <FormInput className="h-5 w-5" />
            <span className="text-xs">Űrlap</span>
          </TabsTrigger>
          <TabsTrigger value="blog" className="flex flex-col items-center gap-2 flex-1">
            <NewspaperIcon className="h-5 w-5" />
            <span className="text-xs">Blog</span>
          </TabsTrigger>
        </TabsList>

        <Card className="mt-4 p-4 bg-white/50 backdrop-blur-sm border-slate-200/60 px-[10px] py-[10px] my-[10px]">
          <TabsContent value="settings" className="animate-slide-in">
            <GeneralTabContent data={funnelData} onUpdate={updateFunnelData} handleImageUpload={handleImageUpload} />
          </TabsContent>

          <TabsContent value="pages" className="space-y-4 animate-slide-in">
            <PagesList pages={funnelData.pages} onChange={pages => updateFunnelData({
            pages
          })} isPro={funnelData.isPro || false} onPageSelect={onPageSelect} finishPageId={funnelData.finishPageId || ""} />
          </TabsContent>

          <TabsContent value="design" className="space-y-6 animate-slide-in">
            <ThemeSelector selectedTheme={funnelData.theme} isPro={funnelData.isPro} isUpgrading={isUpgrading} showBadge={funnelData.showBadge} onThemeSelect={theme => updateFunnelData({
            theme
          })} onUpgrade={handleUpgrade} onBadgeToggle={show => updateFunnelData({
            showBadge: show
          })} />
          </TabsContent>

          <TabsContent value="blog" className="animate-slide-in">
            <BlogTab posts={funnelData.blogPosts || []} onUpdate={posts => updateFunnelData({
            blogPosts: posts
          })} isPro={funnelData.isPro || false} pages={funnelData.pages} onBlogPageSelect={handleBlogPageChange} selectedBlogPageId={funnelData.blogPageId} />
          </TabsContent>

          <TabsContent value="form" className="animate-slide-in">
            <FormElements elements={funnelData.formElements} onChange={elements => updateFunnelData({
            formElements: elements
          })} afterSubmit={funnelData.afterSubmit} onAfterSubmitChange={settings => updateFunnelData({
            afterSubmit: {
              ...funnelData.afterSubmit,
              ...settings
            }
          })} pages={funnelData.pages} onFinishPageChange={handleFinishPageChange} finishPageId={funnelData.finishPageId} siteTitle={funnelData.siteTitle} onSiteTitleChange={title => updateFunnelData({
            siteTitle: title
          })} />
          </TabsContent>
        </Card>
      </Tabs>
    </div>;
};
